import { useEffect, useRef, useCallback } from 'react';

/**
 * useActivityDetection Hook
 *
 * Tracks real user activity (mouse, keyboard, scroll, touch) to determine
 * if the user is actively using the application. This is the core of the
 * inactivity timeout system.
 *
 * Features:
 * - Throttled event detection (prevents excessive updates)
 * - Cross-tab activity awareness
 * - Automatic cleanup on unmount
 * - Configurable activity events
 *
 * PERFORMANCE CONTRACT: this hook renders NOTHING and holds NO state, so it
 * never re-renders its host. That matters because the only consumer (useAuth)
 * is instantiated inside AuthLayout, which renders `<Outlet />` — i.e. the
 * whole authenticated app. A previous version kept `lastActivity` /
 * `timeSinceActivity` in useState and ticked once a second, re-rendering the
 * entire tree every second AND on every tap; on mobile that saturated the main
 * thread and taps were dropped mid-commit (#2492). Read the timestamp through
 * `getLastActivity()` from an interval or event handler, and use `onActivity`
 * for side effects — never mirror it back into render state.
 */

interface UseActivityDetectionOptions {
  /** Events to track as activity (default: mousedown, keydown, scroll, touchstart, click) */
  events?: string[];
  /** Throttle interval in ms (default: 1000) */
  throttleMs?: number;
  /** Enable activity detection (default: true) */
  enabled?: boolean;
  /**
   * Called after a (throttled) *local DOM* activity event is recorded. Runs
   * outside React's render cycle — the right place to broadcast to other tabs.
   * Deliberately NOT called by `triggerActivity()`: activity replayed from
   * another tab goes through `triggerActivity`, and re-broadcasting it would
   * make two tabs ping-pong forever (#2492). Reads latest closure, no need to
   * memoize.
   */
  onActivity?: (timestamp: number) => void;
}

interface UseActivityDetectionReturn {
  /** Timestamp of last detected activity */
  getLastActivity: () => number;
  /**
   * Record activity without notifying `onActivity` — for programmatic events
   * (session start, "extend session", activity replayed from another tab).
   * Callers that also want other tabs told must broadcast explicitly.
   */
  triggerActivity: (timestamp?: number) => void;
}

const DEFAULT_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

export const useActivityDetection = (
  options: UseActivityDetectionOptions = {},
): UseActivityDetectionReturn => {
  const { events = DEFAULT_EVENTS, throttleMs = 1000, enabled = true, onActivity } = options;

  const throttleTimerRef = useRef<number | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Callers pass an inline arrow, so keep the latest in a ref — otherwise it
  // would have to be a dependency of the listener effect and every render
  // would tear down and re-attach the window listeners.
  const onActivityRef = useRef(onActivity);
  onActivityRef.current = onActivity;

  const getLastActivity = useCallback(() => lastActivityRef.current, []);

  // Record activity without notifying React or other tabs. Bypasses the
  // throttle on purpose: it's called for discrete intentional events, not for
  // the event firehose. Never moves the timestamp backwards.
  const triggerActivity = useCallback((timestamp?: number) => {
    lastActivityRef.current = Math.max(lastActivityRef.current, timestamp ?? Date.now());
  }, []);

  // Throttled local-DOM activity handler — the only path that fires onActivity.
  const handleActivity = useCallback(() => {
    // Ignore while a throttle window is open — one activity ping per
    // `throttleMs` is plenty for a 30-minute idle timeout.
    if (throttleTimerRef.current !== null) return;

    const now = Date.now();
    lastActivityRef.current = now;
    onActivityRef.current?.(now);

    throttleTimerRef.current = window.setTimeout(() => {
      throttleTimerRef.current = null;
    }, throttleMs);
  }, [throttleMs]);

  // `events` is an array literal at most call sites, so it has a fresh
  // identity every render. Key the effect on its contents, not its reference.
  const eventsKey = events.join(',');

  // Setup activity listeners
  useEffect(() => {
    if (!enabled) return;

    const trackedEvents = eventsKey.split(',').filter(Boolean);

    // Add passive listeners for better performance
    const eventOptions = { passive: true };

    trackedEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, eventOptions);
    });

    // Cleanup on unmount
    return () => {
      trackedEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });

      // Clear throttle timer
      if (throttleTimerRef.current !== null) {
        clearTimeout(throttleTimerRef.current);
        throttleTimerRef.current = null;
      }
    };
  }, [enabled, eventsKey, handleActivity]);

  return {
    getLastActivity,
    triggerActivity,
  };
};
